import React, { useState } from 'react';
import { Eye, User, Calendar, MessageSquare, Bot } from 'lucide-react';
import TranscriptModal from './TranscriptModal';

const TranscriptView = ({ instances }) => {
    const [selectedInstance, setSelectedInstance] = useState(null);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-4">
            {instances.length === 0 ? (
                <div className="text-center py-12">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Completed Responses</h3>
                    <p className="text-gray-500">Transcripts will appear here once employees complete the survey.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {instances.map((instance) => (
                        <div
                            key={instance.id}
                            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer"
                            onClick={() => setSelectedInstance(instance)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                        <User className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-gray-900 mb-1">
                                            {instance.employee_name || `Employee #${instance.employee_id}`}
                                        </h4>
                                        <p className="text-sm text-gray-500 mb-2">{instance.employee_email}</p>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-3.5 h-3.5" />
                                                <span>Completed: {formatDate(instance.completed_at)}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <MessageSquare className="w-3.5 h-3.5" />
                                                <span>{instance.responses?.length || 0} responses</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedInstance(instance);
                                    }}
                                >
                                    <Eye size={16} />
                                    View Transcript
                                </button>
                            </div>

                            {/* Preview of first response */}
                            {instance.responses && instance.responses.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-start gap-3">
                                        <Bot className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                {instance.responses[0].snapshot_question_text || instance.responses[0].question_text}
                                            </p>
                                            <p className="text-sm text-gray-800 mt-1 line-clamp-2">
                                                <span className="font-medium">Response:</span> {instance.responses[0].response_text}
                                            </p>
                                        </div>
                                    </div>
                                    {instance.responses.length > 1 && (
                                        <p className="text-xs text-indigo-600 mt-2 font-medium">
                                            + {instance.responses.length - 1} more responses
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Transcript Modal */}
            {selectedInstance && (
                <TranscriptModal
                    isOpen={!!selectedInstance}
                    onClose={() => setSelectedInstance(null)}
                    responses={selectedInstance.responses || []}
                    employeeName={selectedInstance.employee_name || 'Employee'}
                    employeeEmail={selectedInstance.employee_email}
                    completedAt={selectedInstance.completed_at}
                    instanceId={selectedInstance.id}
                />
            )}
        </div>
    );
};

export default TranscriptView;
